/**
 * Hook para navegación "Volver" usando el historial persistido en cookie.
 * @module hooks/useNavigationHistory
 */

import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigationHistoryService } from '@/services/navigationHistoryService';

/**
 * Graba la ruta actual en el historial (pathname + search).
 * Debe ejecutarse una vez por cambio de ruta; se usa en NavigationHistoryTracker.
 */
export function useRecordNavigationHistory(): void {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + (location.search || '');
    navigationHistoryService.pushPath(path);
  }, [location.pathname, location.search]);
}

interface UseNavigationHistoryOptions {
  /** Ruta por defecto si no hay historial (ej. /crm/contacts, /crm/contracts) */
  fallbackPath?: string;
}

interface UseNavigationHistoryResult {
  /** Navega a la ruta anterior del historial, o a fallbackPath si no hay */
  goBack: () => void;
  /** true si hay al menos una ruta anterior en el historial */
  canGoBack: boolean;
  /** Ruta anterior (sin modificar historial), útil para etiquetas */
  previousPath: string | null;
}

/**
 * Hook para usar en páginas con botón "Volver".
 * goBack() usa el historial en cookie; si no hay anterior, navega a fallbackPath.
 */
export function useNavigationHistory(options: UseNavigationHistoryOptions = {}): UseNavigationHistoryResult {
  const navigate = useNavigate();
  const fallbackPath = options.fallbackPath ?? '/';

  const goBack = useCallback(() => {
    const previous = navigationHistoryService.popAndGetPrevious();
    if (previous) {
      navigate(previous);
    } else {
      navigate(fallbackPath);
    }
  }, [navigate, fallbackPath]);

  const canGoBack = navigationHistoryService.hasPrevious();
  const previousPath = navigationHistoryService.getPreviousPath();

  return { goBack, canGoBack, previousPath };
}
